import React from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { Button } from "@/components/ui/button";
import { localized } from "@/theme/tenantTheme";
import { rupees } from "@/lib/format";
import { waHref } from "@/lib/phone";

/** C2 (kirana/grocery): a persistent cart summary card in the storefront aside, per
 * `design_handoff_business_websites/Kirana Website.dc.html` — line items, subtotal and a
 * "Checkout · ₹X" CTA, plus a prefilled `wa.me` order link (never an auto-send — the customer
 * taps it themselves, same as every other WhatsApp touchpoint on this site).
 * `07_storefront_design_gap.md` §2 ("Cart / checkout strip"). Reuses the SAME `cart` state
 * `MenuSection`/`Cart.jsx` already read — no parallel cart. Renders only when the visitor's cart
 * belongs to THIS business (a citizen can only ever have one open cart at a time). */
export function CartSummaryCard({ business }) {
  const { t, lang, cart } = useApp();
  const navigate = useNavigate();
  if (!cart || cart.business_id !== business.id || cart.items.length === 0) return null;

  const subtotal = cart.totals?.subtotal ?? 0;
  const waMessage =
    `${t("commerce.orderOnWhatsappPrefill", { business: business.name })}\n` +
    cart.items.map((i) => `• ${localized(lang, i, "name")} × ${i.quantity}`).join("\n") +
    `\n${t("commerce.subtotal")}: ${rupees({ minor_units: subtotal })}`;
  // `null` (no button) for a missing or invalid number - never a wrong `wa.me/98…` chat.
  const waLink = business.whatsapp_number ? waHref(business.whatsapp_number, waMessage) : null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="storefront-cart-summary">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-[15px] font-semibold">{t("commerce.yourCart")}</h3>
        <span className="text-xs text-muted-foreground" data-testid="storefront-cart-summary-count">
          {t("commerce.itemCount", { count: cart.items.length })}
        </span>
      </div>
      <div className="mt-3 flex flex-col gap-2 text-sm">
        {cart.items.map((item) => (
          <div key={item.id} className="flex justify-between gap-2" data-testid={`storefront-cart-summary-line-${item.id}`}>
            <span className="truncate text-muted-foreground">
              {localized(lang, item, "name")} × {item.quantity}
            </span>
            <span className="shrink-0 tabular-nums font-medium">
              {rupees({ minor_units: item.unit_price_minor_units * item.quantity })}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-between border-t border-dashed border-border pt-3 text-sm font-bold">
        <span>{t("commerce.subtotal")}</span>
        <span className="tabular-nums" data-testid="storefront-cart-summary-subtotal">
          {rupees({ minor_units: subtotal })}
        </span>
      </div>
      <Button
        className="mt-3 w-full rounded-full"
        data-testid="storefront-cart-checkout-btn"
        onClick={() => navigate("/cart")}
      >
        {t("commerce.checkoutFor", { amount: rupees({ minor_units: subtotal }) })}
      </Button>
      {waLink && (
        <Button asChild variant="outline" className="mt-2 w-full rounded-full" data-testid="storefront-cart-whatsapp-btn">
          <a href={waLink} target="_blank" rel="noreferrer">
            <MessageCircle size={15} className="mr-2 text-[hsl(var(--primary))]" />
            {t("commerce.orderOnWhatsapp")}
          </a>
        </Button>
      )}
    </div>
  );
}
